import React, { useState, useEffect } from 'react';
import { countries as euVatCountries } from 'jsvat';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input, Table, Select, Button, Checkbox, Result, message, Spin } from 'antd';
import { getData } from 'country-list';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import {
  CopyOutlined
} from '@ant-design/icons';

import {
  getUser, createMollieOrder
} from 'store/ducks/users.js';

const countries = getData().map((obj) => {
  return {
    label: obj.name,
    value: obj.code
  };
});

const ordersColumns = [{
  title: '_id',
  dataIndex: '_id'
}, {
  title: 'Mollie id',
  dataIndex: ['order', 'id']
}, {
  title: 'Amount',
  dataIndex: ['order', 'amount']
}, {
  title: 'Vat Amount',
  dataIndex: ['order', 'vatAmount']
}, {
  title: 'Status',
  dataIndex: ['order', 'status']
}, {
  title: 'Created At',
  dataIndex: ['order', 'createdAt']
}];

const euCountries = euVatCountries.map((country) => country.codes[0]);

const layout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 8 },
};

const validateMessages = {
  required: '${label} is required!'
};

const MollieOrder = () => {
  const dispatch = useDispatch();

  const user = useSelector(state => state.users.user);

  const [fields, setFields] = useState([]);
  const [vatRate, setVatRate] = useState(0);
  const [amount, setAmount] = useState(0);
  const [success, setSuccess] = useState(false);
  const [response, setResponse] = useState({});
  const [vatEu, setVatEu] = useState('');
  const [loading, setLoading] = useState(false);

  const onValuesChange = (values) => {
    if (values.amount) setAmount(parseFloat(values.amount));
    if (values.vatEu) setVatEu(values.vatEu);
    if (values.billingAddress && values.billingAddress.country) setVatRate(euCountries.indexOf(values.billingAddress.country) === -1 ? 0 : 21);
  };

  const onFinish = (values) => {
    if (loading) return;

    setLoading(true);
    dispatch(createMollieOrder({
      ...values,
      user: {
        _id: user._id
      },
      vatRate
    })).then((res) => {
      setResponse(res);
      setSuccess(true);
      dispatch(getUser(user._id));
      setLoading(false);

      if (res.email) {
        message.success('Email with payment link successfully sent!');
      }
    });
  };

  useEffect(() => {
    let fields = [];

    if (user.lifeTime && user.lifeTime.billingAddress) {
      fields = [...fields, {
        name: ['billingAddress', 'givenName'],
        value: user.lifeTime.billingAddress.givenName
      }, {
        name: ['billingAddress', 'familyName'],
        value: user.lifeTime.billingAddress.familyName
      }, {
        name: ['billingAddress', 'streetAndNumber'],
        value: user.lifeTime.billingAddress.streetAndNumber
      }, {
        name: ['billingAddress', 'postalCode'],
        value: user.lifeTime.billingAddress.postalCode
      }, {
        name: ['billingAddress', 'city'],
        value: user.lifeTime.billingAddress.city
      }, {
        name: ['billingAddress', 'country'],
        value: user.lifeTime.billingAddress.country
      }, {
        name: ['billingAddress', 'email'],
        value: user.email
      }];

      setVatRate(euCountries.indexOf(user.lifeTime.billingAddress.country) === -1 ? 0 : 21);
    }

    fields.push({
      name: ['addToLifeTime'],
      value: true
    }, {
      name: ['sendEmail'],
      value: true
    }, {
      name: ['vatEu'],
      value: user.lifeTime.vatEu || ''
    }, {
      name: ['paymentNumber'],
      value: user.lifeTime.orders.length + 1 || 1
    }, {
      name: ['users'],
      value: user.lifeTime.users || 0
    });

    setFields(fields);
  }, [user]);

  return (
    <>
      {success && response && <Result
        status="success"
        title="Successfully Created Mollie Order!"
        subTitle={<>
          <span style={{ margin: '0 10px 0 0' }}>{response.links.checkout.href}</span>
          <CopyToClipboard text={response.links.checkout.href} onCopy={() => message.success('Copied!')}><CopyOutlined style={{ cursor: 'pointer', color: '#008dff' }} /></CopyToClipboard>
        </>}
        extra={[
          <Table style={{ margin: '20px 0 0 0' }} columns={ordersColumns} dataSource={[response]} pagination={false} />
        ]}
      />}

      {!success && <Spin spinning={loading} size="large">
        <Form {...layout} fields={fields} name="nest-messages" onFinish={onFinish} onValuesChange={onValuesChange} validateMessages={validateMessages}>
          <Form.Item name={['billingAddress', 'email']} label="Email" rules={[{ required: true }]}>
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name={['billingAddress', 'givenName']} label="First Name" rules={[{ required: true }]}>
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name={['billingAddress', 'familyName']} label="Last Name" rules={[{ required: true }]}>
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name={['billingAddress', 'streetAndNumber']} label="Street and number" rules={[{ required: true }]}>
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name={['billingAddress', 'postalCode']} label="Postal code" rules={[{ required: true }]}>
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name={['billingAddress', 'city']} label="City" rules={[{ required: true }]}>
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name={['billingAddress', 'country']} label="Country" rules={[{ required: true }]}>
            <Select showSearch
              placeholder="Select a country"
              optionFilterProp="children">
              {countries.map((country) => <Select.Option key={country.value} value={country.value}>{country.label}</Select.Option>)}
            </Select>
          </Form.Item>

          <Form.Item name={['paymentNumber']} label="Payment number" rules={[{ required: true }]} style={{ padding: '20px 0 0 0' }}>
            <Select placeholder="Select a payment number" optionFilterProp="children">
              <Select.Option key={1} value={1}>1</Select.Option>
              <Select.Option key={2} value={2}>2</Select.Option>
              <Select.Option key={3} value={3}>3</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name={['users']} label="Extra users" rules={[{ required: true }]} style={{ padding: '20px 0 0 0' }}>
            <Select placeholder="Select a number of extra users" optionFilterProp="children">
              <Select.Option key={0} value={0}>0</Select.Option>
              <Select.Option key={2} value={1}>1</Select.Option>
              <Select.Option key={2} value={2}>2</Select.Option>
              <Select.Option key={3} value={3}>3</Select.Option>
              <Select.Option key={4} value={4}>4</Select.Option>
              <Select.Option key={5} value={5}>5</Select.Option>
              <Select.Option key={6} value={6}>6</Select.Option>
              <Select.Option key={7} value={7}>7</Select.Option>
              <Select.Option key={8} value={8}>8</Select.Option>
              <Select.Option key={9} value={9}>9</Select.Option>
              <Select.Option key={10} value={10}>10</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name={['vatEu']} label="Vat EU number" rules={[{ required: false }]}>
            <Input autoComplete="off" />
          </Form.Item>

          <Form.Item name={['amount']} label="Amount" rules={[{ required: true }]}>
            <Input autoComplete="off" prefix="$" suffix="USD" />
          </Form.Item>

          <Form.Item label="Vat" rules={[{ required: true }]}>
            <Input autoComplete="off" prefix="$" suffix="USD" value={(vatEu || vatRate === 0) ? 0 : amount * ((vatRate) / 100 + 1)} disabled={true} />
          </Form.Item>

          <Form.Item wrapperCol={{ ...layout.wrapperCol, offset: 4 }} name="addToLifeTime" valuePropName="checked" style={{ margin: '0 0 0 0' }}>
            <Checkbox>Connect with lifeTime</Checkbox>
          </Form.Item>

          <Form.Item wrapperCol={{ ...layout.wrapperCol, offset: 4 }} name="sendEmail" valuePropName="checked">
            <Checkbox>Send email</Checkbox>
          </Form.Item>

          <Form.Item wrapperCol={{ ...layout.wrapperCol, offset: 4 }}>
            <Button type="primary" disabled={loading} htmlType="submit">
              Create Mollie Order
          </Button>
          </Form.Item>
        </Form>
      </Spin>}
    </>
  );
}

export default MollieOrder;
